import "reflect-metadata";

import { type FC, useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { DIContainer } from "../../packages/idn-media-framework-tsyringe/DIContainer";
import { IEventBus } from "../../packages/idn-media-framework-abstraction/IEventBus";
import { IAuthenticationService } from "../../packages/idn-media-framework-abstraction/IAuthenticationService";
import { OAuthFlow } from "../../components/helpers/OAuthFlow";
import { ILogger } from "../../packages/idn-media-framework-abstraction/ILogger";

import { getIdentityType, userIdenityType } from "@/utility/identityType";
import { getPortal } from "@/utility/getPortal";
import { getPlatform, getDevice, getOS } from "@/utility/userAgent";
import {
  OTPActivityVerifiedUser,
  OTPSessionID,
  ResetPasswordSessionID,
} from "@/constants/index";
import { saveSessionStorage } from "@/utility/saveSessionStorage";
import { useRouter } from "next/router";

import { I18n } from "aws-amplify";
import { CustomErrors } from "@/constants/customErrorStrings";
import { customErrorMessages } from "@/constants/customErrorMessages";

import { CognitoUser } from "@aws-amplify/auth";
import { GetPageLink } from "@/utility/getLinkPage";

import Layout from "layouts";
import {
  ButtonOutline,
  ButtonPrimary,
  InputPassword,
  InputText,
} from "@/components/Form";
import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { Link } from "@/components/Navigation";
import { Body, Paragraph } from "@/components/Typography";

import Alert from "@/components/Alert";
import { useProfileDetail } from "hooks/adapters";
import { getOnboardingToggle } from "@/utility/onboarding";
import Head from "next/head";
import { ProfileDetail } from "api-response";
import { useSetupTwoFAStore } from "../SetupTwoFA/useSetupTwoFAStore";
import TwoFA from "./TwoFA";
import { useTwoFAStore } from "./TwoFA/useTwoFAStore";
import { Else, If, Then } from "react-if";
import Modal from "@/components/Modal";
import ModalHeader from "@/components/Modal/ModalHeader";
import Image from "next/image";

// form validation regex
const MIN_CHAR_VLD = 8;
const CHAR_VLD = /^(?=.*[a-z])(?=.*[A-Z]).*$/;
const NUM_VLD = /(?=.*[0-9]).*$/;
const SPCL_VLD = /(?=[A-Za-z0-9!@#$%^&*()]+$)^(?=.*[!@#$%^&*()]).*$/;

const Login: FC = () => {
  const beginSetupTwoFA = useSetupTwoFAStore((s) => s.beginSetupTwoFA);
  const set2FARedirectionUrl = useSetupTwoFAStore((s) => s.setRedirectionUrl);
  const isOnTwoFA = useTwoFAStore((s) => s.isOnTwoFA);
  const confirmedUser = useTwoFAStore((s) => s.confirmedUser);
  const resetTwoFAState = useTwoFAStore((s) => s.resetTwoFAState);
  const beginProcessTwoFA = useTwoFAStore((s) => s.beginProcessTwoFA);

  const { isLoading: isLoadingProfileDetail, response: profileDetail } =
    useProfileDetail();

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [isButtonSubmitDisabled, setIsButtonSubmitDisabled] = useState(true);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [validationIdentityErr, setValidationIdentityErr] = useState("");
  const [alertMsg, setAlertMsg] = useState("");
  const [, setAlertMsgSocmed] = useState("");
  const [loadingButton, setLoadingButton] = useState(false);

  const [user, setUser] = useState<null | any>(null); // state for cognitoUser object

  // state for force change password page
  const [newPassword, setNewPassword] = useState("");
  const [retypeNewPassword, setRetypeNewPassword] = useState("");
  const [errors, setErrors] = useState(new Map());
  const [name, setName] = useState("");
  const [, setIsNewPasswordRequiredChallange] = useState(false);
  const [, setLoadingButtonNewPassword] = useState(false);
  const [typingPassTimeout, setTypingPassTimeout] = useState(0);
  const [typingRetypePassTimeout, setTypingRetypePassTimeout] = useState(0);
  const [, setIsButtonSubmitChangePasswordDisabled] = useState(true);
  // set loading state on first page load
  const [, setIsLoadingPage] = useState(true);

  const router = useRouter();

  const handleLoginButtonSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    setLoadingButton(true);
    e.preventDefault();
    setAlertMsg("");

    const data = e.target as typeof e.target & {
      password: { value: string };
    };

    handleLoginWithUsernameAndPassword(identity, data.password.value);

    // const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
    // eventBus.Dispatch('LoginForm', {eventName: 'LoginWithEmailAndPasswordSubmitted', data: { email: identity, password: data.password.value}, message: ''})
  };

  const { isOpen, onClose, onOpen } = useDisclosure();

  const handleIdpSignIn = async (providerName: any) => {
    const logger = DIContainer.Resolve<ILogger>("ILogger");
    logger.debug("SocialLoginButton clickHandler called");

    const eventBus = DIContainer.Resolve<IEventBus>("IEventBus");
    eventBus.Dispatch("LoginForm", {
      eventName: "SocialLoginButtonClicked",
      data: providerName,
      message: "",
    });
  };

  const handleLoginWithUsernameAndPassword = async (
    identity: string,
    password: string
  ) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    try {
      const user = await Auth.signIn(identity, password, clientMetaData);
      setUser(user);

      // Handle force change password
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        setIsNewPasswordRequiredChallange(true);
        setLoadingButton(false);
        return;
      }

      // Handle 2FA
      if (
        user.challengeName === "SOFTWARE_TOKEN_MFA" ||
        user.challengeName === "SMS_MFA"
      ) {
        await beginProcessTwoFA(user);
        setUser(confirmedUser);
      }

      const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

      const extraReq = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getAccessToken()
            .getJwtToken()}`,
        },
      };

      let response: { data?: ProfileDetail } = {};
      try {
        response = (await API.get("profile", "/detail", extraReq)) as {
          data?: ProfileDetail;
        };
      } catch (err) {
        console.error("Gagal ambil data profil:", err);
      }
      const profileDetail = response.data;

      const url = `/api/config/onboarding-toggle`;
      const config = await fetch(url)
        .then((response) => response.json())
        .catch((e) => console.log(e));

      const toggle = getOnboardingToggle(profileDetail, config);

      if (OAuthFlowSignIn?.isOAuthFlow === false) {
        setIsLoadingPage(false);
        if (
          response?.data?.is_onboarding === false &&
          router.pathname !== "/onboarding" &&
          toggle === "on"
        ) {
          if (
            profileDetail?.is_2fa_enabled === true &&
            profileDetail?.is_2fa_verified === false
          ) {
            set2FARedirectionUrl("/onboarding");
            await beginSetupTwoFA();
          }
          router.push(`/onboarding`);
        } else {
          if (
            profileDetail?.is_2fa_enabled === true &&
            profileDetail?.is_2fa_verified === false
          ) {
            await beginSetupTwoFA();
          }
          setIsLoggedIn(true);
        }
      } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
        setIsLoadingPage(true);
        if (OAuthFlowSignIn.redirectUrl) {
          if (response?.data?.is_onboarding === false && toggle === "on") {
            const encodedUrl = encodeURIComponent(OAuthFlowSignIn.redirectUrl);
            const redirectUri = `/onboarding?redirect=${encodedUrl}`;

            if (
              profileDetail?.is_2fa_enabled === true &&
              profileDetail?.is_2fa_verified === false
            ) {
              set2FARedirectionUrl(redirectUri);
              await beginSetupTwoFA();
            }

            window.location.replace(redirectUri);
          } else {
            if (
              profileDetail?.is_2fa_enabled === true &&
              profileDetail?.is_2fa_verified === false
            ) {
              set2FARedirectionUrl(OAuthFlowSignIn.redirectUrl);
              await beginSetupTwoFA();
            }

            window.location.replace(OAuthFlowSignIn.redirectUrl);
          }
        }
      }

      setIsLoggedIn(true);
      setLoadingButton(false);
      setPassword("");
    } catch (error: any) {
      if (error.response?.data === "Invalid authorization_code") {
        setIsLoggedIn(true);
      } else if (error.code === "UserNotConfirmedException") {
        resendConfirmationSignUpCode(identity);
      } else if (error.code === "PasswordResetRequiredException") {
        userForceResetPasswordRequired(identity);
      } else {
        let errMsg = CustomErrors.DEFAULT_MSG;
        let bannedMsg = error.response?.data?.message || error.message;
        if (bannedMsg?.includes("user already banned")) {
          setLoadingButton(false);
          onOpen();
          return;
        }
        if (error.message) {
          if (
            error.message === CustomErrors.USER_NOT_EXIST ||
            error.message === CustomErrors.USER_NOT_EXIST_IDNACCOUNT
          ) {
            if (getIdentityType(identity) === userIdenityType.PHONE_NUMBER) {
              errMsg = I18n.get("UserNotFoundExceptionPhoneNumber");
            } else {
              errMsg = I18n.get("UserNotFoundExceptionEmail");
            }
          } else if (error.message.includes("kamu melebihi batas device")) {
            errMsg =
              "Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan.";
          } else if (customErrorMessages.hasOwnProperty(error.message)) {
            errMsg = I18n.get(error.message);
          }
        }

        setLoadingButton(false);
        setAlertMsg(errMsg);
        setAlertMsgSocmed("");
        setPassword("");
      }
    }
  };

  // Method handle login email/password before refactor
  const old_handleLoginWithUsernameAndPassword = async (
    identity: string,
    password: string
  ) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    // call API Sign In
    Auth.signIn(identity, password, clientMetaData)
      .then(async (user) => {
        setUser(user);
        // showing force change password page to change new password if user status is FORCE_CHANGE_PASSWORD
        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          setIsNewPasswordRequiredChallange(true);
        } else if (getIsAutenticated(user)) {
          // set user to authenticated if cognito session exist
          const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

          const extraReq = {
            headers: {
              Authorization: `Bearer ${(await Auth.currentSession())
                .getAccessToken()
                .getJwtToken()}`,
            },
          };

          let response: { data?: ProfileDetail } = {};

          try {
            response = (await API.get("profile", "/detail", extraReq)) as {
              data?: ProfileDetail;
            };
          } catch (err) {
            console.error("Gagal ambil data profil:", err);
          }
          const profileDetail = response.data;

          // Fetch onboarding toggle
          const url = `/api/config/onboarding-toggle`;
          const config = await fetch(url)
            .then((response) => response.json())
            .catch((e) => console.log(e));

          // Get toggle onboarding on/off
          const toggle = getOnboardingToggle(profileDetail, config);

          // show logout button on sso broker when not part of any OAuth flow, otherwise redirect to client web
          if (OAuthFlowSignIn?.isOAuthFlow === false) {
            // set loading state false and show logout button if user login directly from sso broker
            setIsLoadingPage(false);
            if (
              response?.data?.is_onboarding === false &&
              router.pathname !== "/onboarding" &&
              toggle === "on"
            ) {
              // Init setup 2fa
              if (
                profileDetail?.is_2fa_enabled === true &&
                profileDetail?.is_2fa_verified === false
              ) {
                set2FARedirectionUrl("/onboarding");
                await beginSetupTwoFA();
              }

              router.push(`/onboarding`);
            } else {
              // Init setup 2fa
              if (
                profileDetail?.is_2fa_enabled === true &&
                profileDetail?.is_2fa_verified === false
              ) {
                await beginSetupTwoFA();
              }
              setIsLoggedIn(true);
            }
          } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
            // show loading when user redirect to client
            setIsLoadingPage(true);
            if (OAuthFlowSignIn.redirectUrl) {
              if (response?.data?.is_onboarding === false && toggle === "on") {
                const encodedUrl = encodeURIComponent(
                  OAuthFlowSignIn.redirectUrl
                );
                const redirectUri = `/onboarding?redirect=${encodedUrl}`;

                // Init setup 2fa
                if (
                  profileDetail?.is_2fa_enabled === true &&
                  profileDetail?.is_2fa_verified === false
                ) {
                  set2FARedirectionUrl(redirectUri);
                  await beginSetupTwoFA();
                }

                window.location.replace(redirectUri);
              } else {
                // Init setup 2fa
                if (
                  profileDetail?.is_2fa_enabled === true &&
                  profileDetail?.is_2fa_verified === false
                ) {
                  set2FARedirectionUrl(OAuthFlowSignIn.redirectUrl);
                  await beginSetupTwoFA();
                }

                window.location.replace(OAuthFlowSignIn.redirectUrl);
              }
            }
          }

          // const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
          // eventBus.Dispatch('IAuthenticationService', {eventName: 'signIn', data: user, message: ''})
        }

        setLoadingButton(false);
        setPassword("");
      })
      .catch((error) => {
        // show logout button on sso broker when authorization_code invalid
        if (error.response?.data == "Invalid authorization_code") {
          setIsLoggedIn(true);
        }
        // handle user with status not confirmed
        else if (error.code == "UserNotConfirmedException") {
          resendConfirmationSignUpCode(identity);
          // handle user with requiring reset their passwords
        } else if (error.code == "PasswordResetRequiredException") {
          userForceResetPasswordRequired(identity);
        } else {
          let errMsg = CustomErrors.DEFAULT_MSG;

          if (error.message) {
            if (
              error.message == CustomErrors.USER_NOT_EXIST ||
              error.message == CustomErrors.USER_NOT_EXIST_IDNACCOUNT
            ) {
              if (getIdentityType(identity) == userIdenityType.PHONE_NUMBER) {
                errMsg = I18n.get("UserNotFoundExceptionPhoneNumber");
              } else {
                errMsg = I18n.get("UserNotFoundExceptionEmail");
              }
            } else if (error.message.includes("kamu melebihi batas device")) {
              errMsg =
                "Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan.";
            } else {
              // check error message on defined error message lists to prevent showing a technical error message to user
              if (customErrorMessages.hasOwnProperty(error.message)) {
                errMsg = I18n.get(error.message);
              }
            }
          }

          setLoadingButton(false);
          setAlertMsg(errMsg);
          setAlertMsgSocmed("");
          setPassword("");
        }
      });
  };

  const resendConfirmationSignUpCode = (userIdentity: string) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    Auth.resendSignUp(userIdentity, clientMetaData)
      .then((user) => {
        saveSessionStorage(OTPSessionID, {
          user_identity: userIdentity,
          activity_otp: OTPActivityVerifiedUser,
        });

        let pathname = GetPageLink("/verification", router.query);

        router.push(pathname);
      })
      .catch((error) => {
        let errMsg = CustomErrors.DEFAULT_MSG;

        if (error.message) {
          if (error.message == CustomErrors.ATTEMPT_LIMIT_EXCEEDED) {
            errMsg = I18n.get("LimitExceededResendCodeException");
          } else {
            // check error message on defined error message lists to prevent showing a technical error message to user
            if (customErrorMessages.hasOwnProperty(error.message)) {
              errMsg = I18n.get(error.message);
            }
          }
        }

        setLoadingButton(false);
        setPassword("");
        setAlertMsg(errMsg);
        setAlertMsgSocmed("");
      });
  };

  const userForceResetPasswordRequired = (userIdentity: string) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
      user_identity: userIdentity,
    };

    Auth.forgotPassword(userIdentity, clientMetaData)
      .then((data) => {
        setLoadingButton(false);

        saveSessionStorage(ResetPasswordSessionID, {
          user_identity: userIdentity,
        });

        let pathname = GetPageLink("/reset-password", router.query);

        // redirect to reset password page for input verification code and new password
        router.push(pathname);
      })
      .catch((error) => {
        let errMsg = CustomErrors.DEFAULT_MSG;

        if (error.message) {
          // check error message on defined error message lists to prevent showing a technical error message to user
          if (customErrorMessages.hasOwnProperty(error.message)) {
            errMsg = I18n.get(error.message);
          }
        }

        setLoadingButton(false);
        setPassword("");
        setAlertMsg(errMsg);
        setAlertMsgSocmed("");
      });
  };

  const getIsAutenticated = (user: CognitoUser): boolean => {
    if (
      !user ||
      !user.getSignInUserSession() ||
      !user.getSignInUserSession()!.isValid() // isValid() also verified the Token Signature
    ) {
      return false;
    }
    return true;
  };

  const handleValidateIdentity = (
    e: React.FormEvent<HTMLInputElement>,
    delay: number = 0
  ) => {
    let userIdentity = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();
    setIdentity(userIdentity);
    setValidationIdentityErr("");
    setAlertMsg("");
    setAlertMsgSocmed("");

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setTypingTimeout(
      window.setTimeout(function () {
        if ((e.target as HTMLInputElement).value == "") {
          setValidationIdentityErr("");
          setIsButtonSubmitDisabled(true);
        } else if (
          getIdentityType(userIdentity) == userIdenityType.PHONE_NUMBER
        ) {
          if (userIdentity.substring(0, 1) == "0") {
            userIdentity = "+62" + userIdentity.substring(1);
          } else if (userIdentity.substring(0, 2) == "62") {
            userIdentity = "+" + userIdentity;
          }
          setIdentity(userIdentity);
        } else {
          setValidationIdentityErr("");
        }
      }, delay)
    );
  };

  const handleValidatePasswordValue = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    setAlertMsg("");
    setAlertMsgSocmed("");
    setPassword((e.target as HTMLInputElement).value);

    if (validationIdentityErr == "" && identity !== "" && password !== "") {
      setIsButtonSubmitDisabled(false);
    } else {
      setIsButtonSubmitDisabled(true);
    }
  };

  const submitChangeTemporaryPassword = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    setLoadingButtonNewPassword(true);
    e.preventDefault();

    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    Auth.completeNewPassword(
      user, // the Cognito User Object
      newPassword, // the new password
      // OPTIONAL, the required attributes
      {
        name: name,
      },
      clientMetaData
    )
      .then(async (user) => {
        setLoadingButtonNewPassword(false);
        setName("");
        setNewPassword("");
        setRetypeNewPassword("");
        setIsNewPasswordRequiredChallange(false);
        // at this time the user is logged in if no MFA required
        // router.push('/');

        setIsLoggedIn(true);
        setUser(user);

        const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

        if (OAuthFlowSignIn?.isOAuthFlow === false) {
          // set loading state false and show logout button if user login directly from sso broker
          setIsLoadingPage(false);
          setIsLoggedIn(true);
        } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
          // show loading when user redirect to client
          setIsLoadingPage(true);
          if (OAuthFlowSignIn.redirectUrl) {
            window.location.replace(OAuthFlowSignIn.redirectUrl);
          }
        }

        // const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
        // eventBus.Dispatch('IAuthenticationService', {eventName: 'signIn', data: user, message: ''})
      })
      .catch((error) => {
        setLoadingButtonNewPassword(false);
        setName("");
        setNewPassword("");
        setRetypeNewPassword("");

        let errMsg = CustomErrors.DEFAULT_MSG;

        // show logout button on sso broker when authorization_code invalid
        if (error.response?.data == "Invalid authorization_code") {
          setIsLoggedIn(true);
        } else if (error.message) {
          // check error message on defined error message lists to prevent showing a technical error message to user
          if (customErrorMessages.hasOwnProperty(error.message)) {
            errMsg = I18n.get(error.message);
          }
        }

        setErrors((map) => new Map(map.set("api", errMsg)));
      });
  };

  const validateName = (e: React.FormEvent<HTMLInputElement>) => {
    setName((e.target as HTMLInputElement).value);
  };

  const validateNewPassword = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    setErrors((map) => map.set("api", ""));
    let value = (e.target as HTMLInputElement).value;
    let input = (e.target as HTMLInputElement).name;

    if (input == "retype-new-password") {
      setRetypeNewPassword(value);
      setErrors((map) => new Map(map.set("retypeNewPassword", "")));
      if (typingRetypePassTimeout) {
        clearTimeout(typingRetypePassTimeout);
      }
      setTypingRetypePassTimeout(
        window.setTimeout(() => {
          if (newPassword != value) {
            setErrors(
              (map) =>
                new Map(
                  map.set(
                    "retypeNewPassword",
                    "Oops, password yang kamu masukkan tidak sama."
                  )
                )
            );
          } else {
            setErrors((map) => new Map(map.set("retypeNewPassword", "")));
          }
        }, delay)
      );
    } else if (input == "new-password") {
      setNewPassword(value);
      setErrors((map) => new Map(map.set("newPassword", "")));
      if (typingPassTimeout) {
        clearTimeout(typingPassTimeout);
      }
      setTypingPassTimeout(
        window.setTimeout(() => {
          if (value == "") {
            setErrors((map) => new Map(map.set("newPassword", "")));
            setIsButtonSubmitChangePasswordDisabled(true);
          } else if (value.length < MIN_CHAR_VLD) {
            setErrors(
              (map) =>
                new Map(
                  map.set(
                    "newPassword",
                    "Password kamu kurang dari 8 karakter."
                  )
                )
            );
          } else if (CHAR_VLD.test(value) === false) {
            setErrors(
              (map) =>
                new Map(
                  map.set(
                    "newPassword",
                    "Password harus terdiri dari huruf besar dan huruf kecil."
                  )
                )
            );
          } else if (
            NUM_VLD.test(value) === false ||
            SPCL_VLD.test(value) === false
          ) {
            setErrors(
              (map) =>
                new Map(
                  map.set(
                    "newPassword",
                    "Password wajib ada 1 simbol !@#$%^&*() dan 1 angka."
                  )
                )
            );
          } else {
            setErrors((map) => new Map(map.set("newPassword", "")));
          }

          if (retypeNewPassword.length != 0 && retypeNewPassword != value) {
            setErrors(
              (map) =>
                new Map(
                  map.set(
                    "retypeNewPassword",
                    "Oops, password yang kamu masukkan tidak sama."
                  )
                )
            );
          } else {
            setErrors((map) => new Map(map.set("retypeNewPassword", "")));
          }
        }, delay)
      );
    }
  };

  const handleAgreePolicy = () => {
    onClose();
  };

  useEffect(() => {
    if (isLoadingProfileDetail) setIsButtonSubmitDisabled(true);
  }, [isLoadingProfileDetail]);

  useEffect(() => {
    setIsButtonSubmitChangePasswordDisabled(true);

    if (
      errors.get("newPassword") ||
      errors.get("retypeNewPassword") ||
      retypeNewPassword == "" ||
      newPassword == "" ||
      name == "" ||
      retypeNewPassword != newPassword
    ) {
      setIsButtonSubmitChangePasswordDisabled(true);
    } else {
      setIsButtonSubmitChangePasswordDisabled(false);
    }
  }, [errors, newPassword, retypeNewPassword, name]);

  useEffect(() => {
    setIsButtonSubmitDisabled(true);

    if (validationIdentityErr !== "" || identity == "" || password == "") {
      setIsButtonSubmitDisabled(true);
    } else {
      setIsButtonSubmitDisabled(false);
    }

    // if (router.query.latest_page) {
    //   const latestPage = router.query.latest_page;
    //   setLatestPage(latestPage[0]);
    // } else {
    //   setLatestPage(redirectHomepage(window.location.hostname));
    // }
  }, [validationIdentityErr, password, identity]);

  useEffect(() => {
    // checking when error message from facebook exist
    if (router.query.error_description) {
      const errorDesc = router.query.error_description.toString().trim();

      // handle email permission disabled by user
      if (
        errorDesc == CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK ||
        errorDesc ==
          CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK_PRESIGNUP_TRIGGER ||
        errorDesc == CustomErrors.USER_ALREADY_DELETED ||
        errorDesc == CustomErrors.USER_ALREADY_DELETED_REGISTER_IDNACCOUNT ||
        errorDesc == CustomErrors.USER_ALREADY_DELETED_SIGNIN_IDNACCOUNT
      ) {
        setAlertMsgSocmed(I18n.get(errorDesc));
        // handle when user canceled login process on fb and apple, or hide error for linked user
      } else if (
        errorDesc == CustomErrors.USER_ALREADY_EXIST_REGISTER_IDNACCOUNT
      ) {
        if (getIdentityType(identity) == userIdenityType.PHONE_NUMBER) {
          setAlertMsgSocmed(
            I18n.get(CustomErrors.PHONE_NUMBER_ALREADY_EXIST_REGISTER)
          );
        } else {
          setAlertMsgSocmed(
            I18n.get(CustomErrors.EMAIL_ALREADY_EXIST_REGISTER)
          );
        }
      } else if (
        /password.reset.required.for.the.user/gi.test(
          router.query.error_description.toString()
        )
      ) {
        setAlertMsgSocmed(CustomErrors.SOCMED_SIGNIN_RESET_REQUIRED);
      } else if (
        errorDesc == CustomErrors.ACCESS_DENIED_FACEBOOK ||
        errorDesc == CustomErrors.CANCELLED_AUTHORIZE_APPLE ||
        /already.found.an.entry.for.username/gi.test(
          router.query.error_description.toString()
        )
      ) {
        return;
      } else if (errorDesc.includes("kamu melebihi batas device")) {
        setAlertMsgSocmed(
          "Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan."
        );
      } else {
        setAlertMsgSocmed(CustomErrors.DEFAULT_MSG);
      }
    }
  }, [router.isReady, router.query.error, router.query.error_description]);

  const handleLogout = async () => {
    const service = DIContainer.Resolve<IAuthenticationService>(
      "IAuthenticationService"
    );
    await service.Logout();

    await router.push("/signout");

    setIsLoggedIn(false);
    setUser(null);
    setIsNewPasswordRequiredChallange(false);

    // const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
    // eventBus.Dispatch('IAuthenticationService', {eventName: 'signOut', data: user, message: ''})
  };

  useEffect(() => {
    if (isLoggedIn) router.push(GetPageLink("/", router.query));
  }, [isLoggedIn, router.push, router.asPath]);

  return (
    <Layout backUrl="/" withLogo={!isOnTwoFA} backUrlonClick={resetTwoFAState}>
      <Head>
        <title>Login ke Akun IDN Connect | Masuk dengan Mudah</title>
        <meta
          name="description"
          content="Masuk IDN Connect menggunakan email, nomor telepon, atau media sosial dengan cepat dan aman."
        />
      </Head>
      <If condition={isOnTwoFA}>
        <Then>
          <TwoFA />
        </Then>
        <Else>
          <form
            data-testid="form_login"
            className="mb-6"
            onSubmit={(e) => handleLoginButtonSubmit(e)}
          >
            {alertMsg ? <Alert text={alertMsg} /> : null}
            <Flex mb="16px" direction="column" gap="8px">
              <InputText
                data-testid="field-email"
                name="user_identity"
                errorMessage={validationIdentityErr}
                label="Email/No. HP"
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  handleValidateIdentity(e, 0)
                }
                onBlur={(e: React.FormEvent<HTMLInputElement>) =>
                  handleValidateIdentity(e)
                }
              />
              <InputPassword
                data-testid="field-password"
                name="password"
                label="Password"
                value={password}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  handleValidatePasswordValue(e)
                }
              />
              <Link
                data-testid="link_forgotPassword"
                textAlign="right"
                href="/forgot-password"
              >
                Lupa password
              </Link>
            </Flex>
            <ButtonPrimary
              type="submit"
              data-testid="button-masuk"
              isLoading={loadingButton}
              isDisabled={isButtonSubmitDisabled}
              loadingText="Masuk"
              mb="24px"
              width="100%"
            >
              Masuk
            </ButtonPrimary>
          </form>
          <Paragraph variant="Default14" color="shade04" textAlign="center">
            Belum punya akun?{" "}
            <Link data-testid="link_register" href="/register">
              Buat akun IDN Connect
            </Link>
          </Paragraph>
        </Else>
      </If>
      <Modal
        onClose={onClose}
        isOpen={isOpen}
        noMaxHeight={true}
        disabled={true}
        maxWidth={"327px"}
        hasCloseButton={false}
      >
        <ModalHeader showed={false} />
        <Flex alignItems={"center"} justifyContent={"center"} mx={"auto"}>
          <Image
            width={263}
            height={148}
            alt=""
            src="/assets/images/onboarding/account-already-banned.svg"
          />
        </Flex>
        <Body
          variant="Bold16"
          color="shade01"
          display={"flex"}
          alignItems={"center"}
          my={4}
        >
          Akunmu Telah Diblokir
        </Body>
        <Box
          data-testid="modal_successRegistration"
          className="mx-auto flex flex-col"
        >
          <Box mb={6}>
            <Body variant="Default14" color="shade03">
              Akun ini tidak dapat digunakan lagi karena melakukan pelanggaran:
            </Body>
            <Body
              variant="Default14"
              color="shade03"
              style={{ marginLeft: "1rem" }}
            >
              <li>Menyiarkan ulang konten IDN Live.</li>
              <li>Kirim komentar negatif berulang kali.</li>
              <li>Kirim gambar/chat berisi SARA.</li>
            </Body>
            <Body variant="Default14" color="shade03">
              Silakan baca penjelasan lengkapnya di bawah ini.
            </Body>
          </Box>
          <Link
            href="/my-account/user-guide#account-banned"
            target="_blank"
            rel="noopener noreferrer"
            style={{ width: "100%" }}
          >
            <ButtonPrimary
              data-testid="buttonRedirect_PrivacyPolicy"
              width="100%"
              mb="8px"
            >
              Lihat Selengkapnya
            </ButtonPrimary>
          </Link>

          <ButtonOutline
            onClick={onClose}
            data-testid="buttonClose_accountBanned"
            width="100%"
          >
            Tutup
          </ButtonOutline>
        </Box>
      </Modal>
    </Layout>
  );
};

export default Login;
